import { getShopIdFromLocation } from 'util/appUtils';

const READ_ACTIONS = new Set([
  'payload_providers.discover',
  'payload_providers.get_active',
  'variant_presentation.settings.get',
]);

function generatedMutationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `product-presentation-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const actionPath = (shopId, action) => (
  `/system/shop/${encodeURIComponent(shopId)}/product-presentation/actions/${READ_ACTIONS.has(action) ? 'read' : 'write'}`
);

function unwrap(response) {
  if (!response || typeof response !== 'object') {
    const error = new Error('The product presentation service returned an invalid response.');
    error.code = 'INVALID_RESPONSE';
    throw error;
  }
  if (response.result === 'error') {
    const error = new Error(response.error || 'Product presentation request failed.');
    error.code = response.code || 'PRODUCT_PRESENTATION_REQUEST_FAILED';
    error.details = response.details || [];
    throw error;
  }
  return response;
}

/** One revision coordinator is shared by the two APIs used by one atomic screen. */
export function createLegacyProductPresentationApis({
  client,
  shopId,
  mutationIdFactory = generatedMutationId,
} = {}) {
  let resolvedClient = client || null;
  let revision = null;
  const pending = new Map();
  const getClient = () => {
    if (!resolvedClient) {
      // eslint-disable-next-line global-require
      resolvedClient = require('util/appUtils').wsClient;
    }
    return resolvedClient;
  };
  const getShopId = () => shopId || getShopIdFromLocation();

  async function send(action, payload = {}) {
    const response = unwrap(await getClient().prepareRequest(
      'POST',
      actionPath(getShopId(), action),
      { body: { action, payload } },
    ));
    if (Number.isInteger(Number(response.config_revision))) {
      revision = Number(response.config_revision);
    }
    return response;
  }

  async function ensureRevision() {
    if (revision === null) await send('variant_presentation.settings.get');
    return revision;
  }

  async function mutate(action, payload) {
    const signature = JSON.stringify(payload);
    const previous = pending.get(action);
    const mutation = previous?.signature === signature
      ? previous
      : { signature, id: mutationIdFactory() };
    pending.set(action, mutation);
    const response = await send(action, {
      ...payload,
      expected_revision: await ensureRevision(),
      client_mutation_id: mutation.id,
    });
    pending.delete(action);
    return response;
  }

  return {
    payloadProviderApi: {
      discoverProviders: () => send('payload_providers.discover'),
      getActiveProviderConfigs: () => send('payload_providers.get_active'),
      saveActiveProviderConfigs: providers => mutate('payload_providers.set', {
        providers: providers.map(config => ({
          provider_key: config.provider_key,
          config: config.config !== undefined ? config.config : (config.params || {}),
        })),
      }),
    },
    variantPresentationApi: {
      getSettings: () => send('variant_presentation.settings.get'),
      saveSettings: settings => mutate('variant_presentation.settings.save', settings),
    },
  };
}

export { READ_ACTIONS as PRODUCT_PRESENTATION_READ_ACTIONS };
